import { ApplicationConfig, LOCALE_ID, ErrorHandler } from '@angular/core';
import { GlobalErrorHandler } from './core/global-error.handler';
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { importProvidersFrom } from '@angular/core';
import { environment } from '../environments/environment';
import { provideRouter, withPreloading, PreloadAllModules, TitleStrategy, RouteReuseStrategy } from '@angular/router';
import { RecrearAlCambiarParametrosStrategy } from './core/navegacion-registros/recrear-al-cambiar-parametros.strategy';
import { SoftRentTitleStrategy } from './core/services/soft-rent-title-strategy';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { BadgeModule } from 'primeng/badge';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { tenantInterceptor } from './core/interceptors/tenant.interceptor';
import { operadorInterceptor } from './core/interceptors/operador.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { retryInterceptor } from './core/interceptors/retry.interceptor';
import { PROVEEDOR_CONFIRMACIONES } from './core/services/confirmaciones.service';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { planLimitInterceptor } from './core/interceptors/plan-limit.interceptor';

/**
 * Campos de formulario (input, select, datepicker, inputNumber) con los neutros v16 en vez del gris azulado (slate) de
 * Aura: el borde era #CBD5E1, el placeholder #64748B y el fondo deshabilitado #E2E8F0, también en Onyx. Solo colores:
 * tamaños, radios y sombras siguen siendo los de Aura. Las variables CSS ya cambian solas con `.dark-theme`, así que el
 * mismo juego vale para los dos esquemas.
 */
const CAMPO_V16 = {
  background: 'var(--card)',
  disabledBackground: 'var(--paper-tint)',
  filledBackground: 'var(--paper-tint)',
  filledHoverBackground: 'var(--paper-tint)',
  filledFocusBackground: 'var(--paper-tint)',
  borderColor: 'var(--hairline-strong)',
  hoverBorderColor: 'var(--ink-faint)',
  color: 'var(--ink)',
  disabledColor: 'var(--ink-faint)',
  placeholderColor: 'var(--ink-faint)',
  floatLabelColor: 'var(--ink-faint)',
  floatLabelActiveColor: 'var(--ink-faint)',
  iconColor: 'var(--ink-faint)',
};

/**
 * Preset SoftRent v16: Aura con `primary` = terracotta (#E55408).
 * Alinea PrimeNG al sistema de diseño — elimina el azul/cian por defecto en
 * selects, checkboxes, anillos de foco, etc. (regla v16: `info` = acento, sin azul).
 */
const SoftRentPreset = definePreset(Aura, {
  semantic: {
    colorScheme: {
      light: { formField: CAMPO_V16 },
      dark: { formField: CAMPO_V16 },
    },
    primary: {
      50: '#FDF3EE',
      100: '#FAE0D2',
      200: '#F5C2A5',
      300: '#EF9D6E',
      400: '#EA7637',
      500: '#E55408',
      600: '#C94605',
      700: '#A83A05',
      800: '#87300A',
      900: '#6E2A0C',
      950: '#3C1305',
    },
  },
});

import { registerLocaleData } from '@angular/common';
import localeEsCl from '@angular/common/locales/es-CL';
import { provideServiceWorker } from '@angular/service-worker';

registerLocaleData(localeEsCl);

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'es-CL' },
    // El título de la pestaña lo arma PageTitleService (empresa, aviso de notificaciones, lo que se está viendo).
    { provide: TitleStrategy, useClass: SoftRentTitleStrategy },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: 'America/Santiago' } },
    { provide: ErrorHandler, useClass: GlobalErrorHandler }, // error boundary → /error (F7)

    provideRouter(routes, withPreloading(PreloadAllModules)),
    // Un detalle marcado con data.recrearAlCambiarParametros se crea de nuevo al pasar de /x/5 a /x/6 (navegación entre
    // registros); el resto de las rutas se reutiliza como siempre.
    { provide: RouteReuseStrategy, useClass: RecrearAlCambiarParametrosStrategy },
    provideAnimationsAsync(),

    provideHttpClient(
      withInterceptors([
        tenantInterceptor,   // Dominio del cliente: va primero, sin tenant no hay nada que hacer
        operadorInterceptor, // Qué mecánico tiene la tablet de taller (X-Operador) y qué hacer si su sesión venció
        retryInterceptor,    // Retry automático para cold start (0, 502, 503, 504)
        authInterceptor,     // Agregar token
        loadingInterceptor,  // Spinner de carga global
        errorInterceptor,    // Manejar errores
        planLimitInterceptor // Manejar límites de plan
      ])
    ),
    
    providePrimeNG({
      theme: {
        preset: SoftRentPreset,
        options: {
          prefix: 'p',
          darkModeSelector: '.dark-theme'
        }
      }
    }),
    importProvidersFrom(BadgeModule),
    // Solo en prod real: el build de QAS (production:false) NO compila ngsw-worker.js, así que
    // registrarlo daría 404 → error de MIME 'text/html'. Gatear en environment.production alinea
    // el registro con el único build que genera el SW.
    PROVEEDOR_CONFIRMACIONES, provideServiceWorker('ngsw-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerWhenStable:30000'
          })
  ]
};